import { ctw } from '@/common';
import { QuestionMarkTooltip } from '../QuestionMarkTooltip';
import { BadgeCheck } from '../svg/BadgeCheck';
import { BadgeCritical } from '../svg/BadgeCritical';
import { BadgeUnknown } from '../svg/BadgeUnknown';
import { BadgeWarn } from '../svg/BadgeWarn';
import { POLICY_FIT_STATUS_MAP } from '@ballerine/common';
import type { PolicyFitStatus } from '@ballerine/common';

type PolicyFitProps = {
  policyFit: {
    status?: PolicyFitStatus;
    description?: string | null;
    displayName?: string | null;
    version?: number | null;
  };
};

const policyFitTextMap: Record<PolicyFitStatus, string> = {
  [POLICY_FIT_STATUS_MAP.NEUTRAL]: 'Neutral',
  [POLICY_FIT_STATUS_MAP.RESTRICTED]: 'Restricted',
  [POLICY_FIT_STATUS_MAP.PROHIBITED]: 'Prohibited',
  [POLICY_FIT_STATUS_MAP.UNVERIFIED]: 'Unverified',
};

const getPolicyFitStyles = (status: PolicyFitStatus | null, variant: 'default' | 'simple') => {
  switch (status) {
    case POLICY_FIT_STATUS_MAP.NEUTRAL:
      return { icon: <BadgeCheck variant={variant} />, className: 'bg-green-500' };
    case POLICY_FIT_STATUS_MAP.RESTRICTED:
      return { icon: <BadgeWarn variant={variant} />, className: 'bg-orange-300' };
    case POLICY_FIT_STATUS_MAP.PROHIBITED:
      return { icon: <BadgeCritical variant={variant} />, className: 'bg-red-500' };
    default:
      return {
        icon: <BadgeUnknown variant={variant} />,
        className: 'bg-zinc-100 text-neutral-400',
        iconClassName: 'mb-0',
      };
  }
};

export const PolicyFitIcon = ({
  policyFit,
  variant,
  withStatusText = false,
}: PolicyFitProps & { variant: 'default' | 'simple'; withStatusText?: boolean }) => {
  const { status = POLICY_FIT_STATUS_MAP.UNVERIFIED } = policyFit;
  const { icon } = getPolicyFitStyles(status, variant);

  return (
    <div className="flex gap-2 items-baseline">
      <div className={ctw('-mb-2')}>{icon}</div>
      {withStatusText && <span className="text-slate-800">{policyFitTextMap[status]}</span>}
    </div>
  );
};

export const PolicyFitBadge = ({ policyFit }: PolicyFitProps) => {
  const { status = POLICY_FIT_STATUS_MAP.UNVERIFIED } = policyFit;
  const { className } = getPolicyFitStyles(status, 'default');

  return (
    <div className={ctw('text-white font-semibold py-1 px-2 rounded-lg w-fit', className)}>
      <div className="flex gap-1 items-center">
        <PolicyFitIcon policyFit={policyFit} variant="default" />
        <span>{policyFitTextMap[status]}</span>
      </div>
    </div>
  );
};

type PolicyFitHeaderProps = PolicyFitProps & {
  customer?: {
    displayName?: string | null;
  } | null;
};

export const PolicyFitHeader = ({ policyFit, customer }: PolicyFitHeaderProps) => {
  const { version } = policyFit;
  const displayName = customer?.displayName;

  return (
    <div className="flex-1">
      <div className="flex items-center gap-1 mb-3">
        <h4 className="text-sm font-semibold text-stone-300">YOUR POLICY FIT</h4>
        <QuestionMarkTooltip
          text="Assessment of how well this merchant aligns with your underwriting policies and risk acceptance criteria."
          className="ml-2"
        />
      </div>

      <PolicyFitBadge policyFit={policyFit} />

      {displayName && version != null && (
        <p className="text-sm text-stone-400 mt-1">
          Based on {displayName} v.{version}
        </p>
      )}
    </div>
  );
};
